import { Injectable } from '@angular/core';
import { DynamicFieldConfig, FieldsetConfig } from '../interfaces';
import { MaskService } from './mask.service';

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    constructor(private maskService: MaskService) {}

    /**
     * Gera ID único para um campo
     */
    getFieldId(field: DynamicFieldConfig): string {
        return `field-${field.name}`;
    }

    /**
     * Obtém o grupo de um campo
     */
    getFieldGroup(field: DynamicFieldConfig): string {
        return field.fieldLayout?.group || 'default';
    }

    /**
     * Obtém fieldset por grupo
     */
    getFieldsetByGroup(group: string, fieldsets: FieldsetConfig[]): FieldsetConfig | undefined {
        return fieldsets.find(fieldset => fieldset.group === group);
    }

    /**
     * Obtém campos por grupo
     */
    getFieldsByGroup(group: string, config: DynamicFieldConfig[]): DynamicFieldConfig[] {
        return config.filter(field => this.getFieldGroup(field) === group);
    }

    /**
     * Obtém grupos únicos
     */
    getUniqueGroups(config: DynamicFieldConfig[]): string[] {
        const groups = config.map(field => this.getFieldGroup(field));
        return [...new Set(groups)];
    }

    /**
     * Gera estilo de grid para um campo
     */
    getFieldGridStyle(field: DynamicFieldConfig): any {
        const colSpan = field.fieldLayout?.colSpan || 12;
        const rowSpan = field.fieldLayout?.rowSpan || 1;

        return {
            'grid-column': `span ${colSpan} / span ${colSpan}`,
            'grid-row': rowSpan > 1 ? `span ${rowSpan} / span ${rowSpan}` : 'auto',
        };
    }

    /**
     * Verifica se um campo é readonly
     */
    isFieldReadonly(field: DynamicFieldConfig): boolean {
        return field.readonly ?? false;
    }

    /**
     * Gera autocomplete para um campo
     */
    getFieldAutocomplete(field: DynamicFieldConfig): string {
        // Se já foi definido explicitamente, usar o valor
        if (field.autocomplete) {
            return field.autocomplete;
        }

        // Gerar automaticamente baseado no tipo e nome do campo
        const fieldName = field.name.toLowerCase();
        const fieldLabel = field.label.toLowerCase();

        // Para campos de senha
        if (field.type === 'password') {
            if (fieldName.includes('confirm') || fieldLabel.includes('confirmar')) {
                return 'new-password';
            }
            if (fieldName.includes('current') || fieldLabel.includes('atual')) {
                return 'current-password';
            }
            return 'new-password';
        }

        // Para campos de email
        if (field.type === 'email') {
            return 'email';
        }

        // Para campos de telefone
        if (field.type === 'phone') {
            return 'tel';
        }

        // Para campos de nome
        if (fieldName.includes('nome') || fieldName.includes('name')) {
            if (fieldName.includes('primeiro') || fieldName.includes('first')) {
                return 'given-name';
            }
            if (fieldName.includes('ultimo') || fieldName.includes('last')) {
                return 'family-name';
            }
            return 'name';
        }

        // Para campos de endereço
        if (fieldName.includes('endereco') || fieldName.includes('address')) {
            return 'street-address';
        }

        // Para campos de cidade
        if (fieldName.includes('cidade') || fieldName.includes('city')) {
            return 'address-level2';
        }

        // Para campos de estado
        if (fieldName.includes('estado') || fieldName.includes('state')) {
            return 'address-level1';
        }

        // Para campos de CEP
        if (field.type === 'cep') {
            return 'postal-code';
        }

        // Para campos de CPF
        if (field.type === 'cpf') {
            return 'off';
        }

        // Para campos de CNPJ
        if (field.type === 'cnpj') {
            return 'off';
        }

        // Para campos de data
        if (field.type === 'date') {
            if (fieldName.includes('nascimento') || fieldName.includes('birth')) {
                return 'bday';
            }
            return 'off';
        }

        // Para campos de número
        if (field.type === 'number') {
            return 'off';
        }

        // Para campos de texto genéricos
        if (field.type === 'text') {
            if (fieldName.includes('usuario') || fieldName.includes('username')) {
                return 'username';
            }
            if (fieldName.includes('empresa') || fieldName.includes('company')) {
                return 'organization';
            }
            if (fieldName.includes('cargo') || fieldName.includes('job')) {
                return 'organization-title';
            }
        }

        // Padrão: desabilitar autocomplete
        return 'off';
    }

    /**
     * Gera classes do botão de submit
     */
    getSubmitButtonClasses(submitButtonColor: string, isFormValid: boolean): any {
        const classes: any = {};

        switch (submitButtonColor) {
            case 'primary':
                classes['bg-primary'] = true;
                classes['hover:bg-primary-dark'] = true;
                break;
            case 'success':
                classes['bg-success'] = true;
                classes['hover:bg-success-dark'] = true;
                break;
            case 'danger':
                classes['bg-danger'] = true;
                classes['hover:bg-danger-dark'] = true;
                break;
            case 'secondary':
            default:
                classes['bg-green-600'] = true;
                classes['hover:bg-green-700'] = true;
                break;
        }

        if (!isFormValid) {
            classes['opacity-50'] = true;
            classes['cursor-not-allowed'] = true;
        }
        return classes;
    }
}
